import { useState } from "react";
import * as Lucide from "lucide-react";
import cn from "classnames";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AI_MODEL } from "@/store/zustand/aiActionStore";
import Icon, { ICONS } from "@/components/Core/Icons";
import { useFeatureFlagEnabled } from "posthog-js/react";

const getModelLabel = (model) => {
  switch (model) {
    case AI_MODEL.CLAUDE_3_7_SONNET:
      return "3.7";
    case AI_MODEL.CLAUDE_3_5_SONNET:
      return "3.5";
    case AI_MODEL.GEMINI_2_5_PRO:
      return "2.5";
    case AI_MODEL.GPT_4_1:
      return "4.1";
    case AI_MODEL.CLAUDE_4_0_SONNET:
      return "4.0";
    case AI_MODEL.CLAUDE_4_0_SONNET_MAX:
      return (
        <div className="flex items-center">
          <span>4.0</span>
          <span
            className="ml-2 text-3xs bg-gray-600 rounded font-medium"
            style={{
              background:
                "linear-gradient(90deg, #38C793 0%, #937FF4 50%, #DA41D3 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
              border: "1px solid #2F3035",
              height: "14px",
              padding: "2px",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            MAX
          </span>
        </div>
      );
    default:
      return (
        <div className="flex items-center">
          <span>3.7</span>
          <Icon
            icon={ICONS.GEMINI}
            className="h-4 w-4 text-secondary-900 ml-1"
          />
        </div>
      );
  }
};

const MenuItem = ({ value, label, isSelected, setReasoningModel }) => (
  <DropdownMenuItem
    className="py-1 px-2 h-7 text-xs flex items-center justify-between cursor-pointer"
    onSelect={() => setReasoningModel(value)}
  >
    {label}
    {isSelected && <Lucide.Check className="ml-2 h-4 w-4 text-icons-primary" />}
  </DropdownMenuItem>
);

const AiModelDropdown = ({ reasoningModel, setReasoningModel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const gemini25Enabled = useFeatureFlagEnabled("gemini-2-5-pro");
  const gpt41Enabled = useFeatureFlagEnabled("gpt-4-1");
  const maxAgentEnabled = useFeatureFlagEnabled("tempo-max-agent");

  return (
    <DropdownMenu open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DropdownMenuTrigger asChild>
        <div
          className={cn(
            "h-7 p-0 min-w-[70px] flex items-center text-xs justify-between rounded-md transition-colors bg-secondary-n-950 max-w-[70px] px-2 cursor-pointer",
            isOpen ? "text-secondary-n-50" : "text-text-tertiary"
          )}
        >
          {getModelLabel(reasoningModel)}
          {isOpen ? (
            <Lucide.ChevronUp className="h-4 w-4" />
          ) : (
            <Lucide.ChevronDown className="h-4 w-4" />
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[220px] rounded-lg px-0 pb-2 pt-2 bg-popover text-xs text-white"
      >
        <div className="px-2">
          <MenuItem
            value={AI_MODEL.CLAUDE_4_0_SONNET}
            label="Claude 4.0 Sonnet"
            isSelected={reasoningModel === AI_MODEL.CLAUDE_4_0_SONNET}
            setReasoningModel={setReasoningModel}
          />
          {maxAgentEnabled && (
            <MenuItem
              value={AI_MODEL.CLAUDE_4_0_SONNET_MAX}
              label={
                <div className="flex items-center">
                  <span>Claude 4.0 Sonnet</span>
                  <span
                    className="ml-3 px-1 text-2xs bg-gray-300 rounded font-medium align-baseline"
                    style={{
                      background:
                        "linear-gradient(90deg, #38C793 0%, #937FF4 50%, #DA41D3 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: "transparent",
                      border: "1px solid #2F3035",
                      height: "14px",
                      padding: "2px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    MAX
                  </span>
                </div>
              }
              isSelected={reasoningModel === AI_MODEL.CLAUDE_4_0_SONNET_MAX}
              setReasoningModel={setReasoningModel}
            />
          )}
          <MenuItem
            value={AI_MODEL.CLAUDE_3_7_SONNET}
            label="Claude 3.7 Sonnet"
            isSelected={reasoningModel === AI_MODEL.CLAUDE_3_7_SONNET}
            setReasoningModel={setReasoningModel}
          />
          <MenuItem
            value={AI_MODEL.GEMINI_2_0_FLASH}
            label="Gemini + Claude 3.7 Sonnet"
            isSelected={reasoningModel === AI_MODEL.GEMINI_2_0_FLASH}
            setReasoningModel={setReasoningModel}
          />
          <MenuItem
            value={AI_MODEL.CLAUDE_3_5_SONNET}
            label="Claude 3.5 Sonnet"
            isSelected={reasoningModel === AI_MODEL.CLAUDE_3_5_SONNET}
            setReasoningModel={setReasoningModel}
          />
          {gemini25Enabled && (
            <MenuItem
              value={AI_MODEL.GEMINI_2_5_PRO}
              label="Gemini 2.5 Pro"
              isSelected={reasoningModel === AI_MODEL.GEMINI_2_5_PRO}
              setReasoningModel={setReasoningModel}
            />
          )}
          {gpt41Enabled && (
            <MenuItem
              value={AI_MODEL.GPT_4_1}
              label="GPT 4.1"
              isSelected={reasoningModel === AI_MODEL.GPT_4_1}
              setReasoningModel={setReasoningModel}
            />
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AiModelDropdown;
